/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// THE CLIENT'S LEGAL FORM (v1_832). What the client IS in law, kept apart from
// the entity type (what the business DOES: TECHNOLOGY, VASP, ASSET_MANAGER).
// It places a case in the policy questionnaire's partnership, trust or
// individual group (section 4 document lists, section 3 thresholds) and opens
// the v1_831 gate for the three client types it defines (section 1).
//
// Where it comes from, first found wins (decisions 26/09/26):
//   1. officer   the MLRO's pin (collected_data.legalFormPin), with a reason;
//   2. invitation the officer said at invitation the client is an individual
//                (collected_data.clientKind = 'individual');
//   3. documents the analysis read it from the documents
//                (report.companyDetails.legalForm);
//                A charity registration number is attributed to where it was
//                read: the MLRO's pin (officer) or the documents (documents),
//                and on its own makes a charity (never a name source);
//   4. name      a backstop on the legal name: LLP or LP as a whole word or at
//                the end; trust, foundation or Stiftung (never on a name that
//                carries a company suffix);
//   5. default   company.
// The source and the basis are recorded on the case (report.legalForm). Pure.

export const LEGAL_FORMS = Object.freeze({
  company: 'Company',
  llp: 'Limited liability partnership',
  limited_partnership: 'Limited partnership',
  ordinary_partnership: 'Ordinary partnership',
  sole_trader: 'Sole trader',
  unincorporated_association: 'Unincorporated association',
  trust: 'Trust',
  foundation: 'Foundation',
  charity: 'Charity',
  individual: 'Individual',
});
export const LEGAL_FORM_KEYS = Object.freeze(Object.keys(LEGAL_FORMS));

// The questionnaire group a legal form places the case in. null: the entity
// type decides, as for any company (an LLP reads the company lists: private
// company, or regulated firm when its entity type is regulated). A sole trader
// reads the individual group and an unincorporated association the partnership
// group (decision 26/09/26 18:04).
export const LEGAL_FORM_GROUP = Object.freeze({
  company: null, llp: null,
  sole_trader: 'individual', individual: 'individual',
  limited_partnership: 'partnership', ordinary_partnership: 'partnership', unincorporated_association: 'partnership',
  trust: 'trust', foundation: 'trust', charity: 'trust',
});

// The sources firm enough to open a client type gate (v1_831) on their own.
// The name backstop places the case in its floor and threshold group but never
// declines a client alone.
export const GATE_SOURCES = Object.freeze(['officer', 'invitation', 'documents']);

// A legal form as the model or an officer wrote it, to a key.
export function normaliseLegalForm(v) {
  const s = String(v || '').trim().toLowerCase().replace(/[^a-z ]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return null;
  if (LEGAL_FORM_KEYS.includes(s.replace(/ /g, '_'))) return s.replace(/ /g, '_');
  if (/\bl ?l ?p\b|limited liability partnership/.test(s)) return 'llp';
  if (/limited partnership|\bl ?p\b/.test(s)) return 'limited_partnership';
  if (/partnership|\bfirm of partners\b/.test(s)) return 'ordinary_partnership';
  if (/sole (trader|proprietor)|self employed/.test(s)) return 'sole_trader';
  if (/unincorporated|club|society|association/.test(s)) return 'unincorporated_association';
  if (/charit/.test(s)) return 'charity';
  if (/foundation|stiftung|fondation|fundaci/.test(s)) return 'foundation';
  if (/\btrust\b/.test(s)) return 'trust';
  if (/individual|natural person|private person|private individual/.test(s)) return 'individual';
  if (/company|limited|\bltd\b|\bplc\b|corporation|incorporated|\binc\b|\bllc\b|gmbh|\bag\b|\bsa\b|\bbv\b|\bnv\b|sarl|\bpte\b|\bpty\b/.test(s)) return 'company';
  return null;
}

const COMPANY_SUFFIX = /\b(ltd|limited|plc|gmbh|ag|sa|s\.?a\.?r\.?l|inc|incorporated|llc|corp|corporation|bv|nv|pte|pty|oy|ab|aps|kft|spa|s\.?p\.?a)\b\.?$/i;
function nameBackstop(name) {
  const n = String(name || '').trim();
  // LLP and LP only as a whole word or at the end of the name, and never on a
  // name that also carries a company suffix ("Help Ltd (LP holdings)").
  const companyWord = /\b(ltd|limited|plc|gmbh|inc|incorporated|llc|corp|corporation|pty|pte)\b/i.test(n);
  if (companyWord) { /* a company: no partnership backstop */ }
  else if (/(^|[\s,(])L\.?L\.?P\.?($|[\s,)])/i.test(n) || /\bLLP\.?$/i.test(n)) return { form: 'llp', basis: `the legal name "${n}" carries LLP` };
  else if (/(^|[\s,(])L\.?P\.?($|[\s,)])/.test(n) || /\bLP\.?$/.test(n)) return { form: 'limited_partnership', basis: `the legal name "${n}" carries LP` };
  if (n && !COMPANY_SUFFIX.test(n)) {
    if (/\b(foundation|stiftung|fondation)\b/i.test(n)) return { form: 'foundation', basis: `the legal name "${n}" names a foundation` };
    if (/\btrust\b/i.test(n)) return { form: 'trust', basis: `the legal name "${n}" names a trust` };
  }
  return null;
}

export function resolveLegalForm({ report, collected, companyName } = {}) {
  const r = report || {}, cd = collected || {};
  const cdet = (r.companyDetails && typeof r.companyDetails === 'object') ? r.companyDetails : {};
  const charityNumber = String((cd.legalFormPin && cd.legalFormPin.charityNumber) || cdet.charityNumber || '').trim() || null;
  const out = (form, source, basis) => ({ form, label: LEGAL_FORMS[form], group: LEGAL_FORM_GROUP[form], source, basis, ...(charityNumber ? { charityNumber } : {}) });
  const pin = cd.legalFormPin && normaliseLegalForm(cd.legalFormPin.form);
  if (pin) return out(pin, 'officer', `set by ${cd.legalFormPin.by || 'the MLRO'}${cd.legalFormPin.reason ? ': ' + String(cd.legalFormPin.reason).slice(0, 200) : ''}`);
  if (String(cd.clientKind || '').toLowerCase() === 'individual') return out('individual', 'invitation', 'the officer said at invitation that the client is an individual');
  const read = normaliseLegalForm(cdet.legalForm);
  if (read) return out(read, 'documents', `read from the documents ("${String(cdet.legalForm).slice(0, 80)}")`);
  // A charity registration number read from the documents is a documents source.
  if (charityNumber) return out('charity', 'documents', `a charity registration number is read from the documents (${charityNumber})`);
  const b = nameBackstop(cdet.legalName || r.clientName || companyName);
  if (b) return out(b.form, 'name', b.basis);
  return out('company', 'default', 'no other legal form is stated on the file');
}

// The case's legal form as stamped at its last rebuild or retier, else resolved.
export function caseLegalForm(report, collected) {
  const st = report && report.legalForm;
  if (st && LEGAL_FORM_KEYS.includes(st.form)) return { ...st, label: LEGAL_FORMS[st.form], group: LEGAL_FORM_GROUP[st.form] };
  return resolveLegalForm({ report, collected });
}

// What is stored on the board.
export const legalFormStamp = (lf) => ({ form: lf.form, source: lf.source, basis: lf.basis, ...(lf.charityNumber ? { charityNumber: lf.charityNumber } : {}) });

// The line the models are given for the board schema.
export const LEGAL_FORM_PROMPT = 'companyDetails.legalForm: the client\'s legal form as the documents state it, one of ' + LEGAL_FORM_KEYS.join(', ') + ' (an LLP is llp; a registered charity is charity); leave it empty when no document states it. companyDetails.charityNumber: a charity registration number where one is on file, else empty.';

// Decision 5: a run reads its threshold from the legal form it started with.
// When the documents read during the run give a different form, the case says
// so, naming both, and the next update applies the new value. null otherwise.
export function legalFormChangeLine(startLf, readLf) {
  if (!startLf || !readLf || readLf.source !== 'documents' || !readLf.form || readLf.form === startLf.form) return null;
  const a = LEGAL_FORMS[startLf.form] || startLf.form, b = LEGAL_FORMS[readLf.form] || readLf.form;
  return {
    from: startLf.form, to: readLf.form,
    line: `The documents read in this update give the client's legal form as ${b.toLowerCase()}, where the update started with ${a.toLowerCase()}. This update read the firm's ownership threshold for ${a.toLowerCase()}; the next update applies ${b.toLowerCase()}.`,
  };
}
